import React from 'react';
import { GreenDot, OrangeDot, RedDot } from '../../../assets/Icons';
import styled from 'styled-components';

interface StatusDotProps {
  status: string;
  type?: 'order' | 'payment' | 'delivery';
}

interface StyledProps {
  type?: 'order' | 'payment' | 'delivery';
  'data-status'?: string;
}

const getStatusColor = (status: string | undefined) => {
  if (!status) return '#666';
  const normalizedStatus = status.toLowerCase();
  if (normalizedStatus.includes('pending')) return '#FFA600';
  if (
    normalizedStatus.includes('completed') ||
    normalizedStatus.includes('ongoing') ||
    normalizedStatus.includes('success')
  )
    return '#008000';
  if (
    normalizedStatus.includes('rejected') ||
    normalizedStatus.includes('cancelled') ||
    normalizedStatus.includes('failed')
  )
    return '#FF0000';
  return '#666';
};

const StatusContainer = styled.div<StyledProps>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: ${(props: StyledProps) => {
    switch (props.type) {
      case 'order':
        return '6px 16px';
      case 'payment':
        return '4px 12px';
      case 'delivery':
        return '4px 8px';
      default:
        return '6px 16px';
    }
  }};
  border-radius: ${(props: StyledProps) => {
    switch (props.type) {
      case 'order':
        return '20px';
      case 'payment':
        return '6px';
      case 'delivery':
        return '4px';
      default:
        return '20px';
    }
  }};
  background: ${(props: StyledProps) => {
    const color = getStatusColor(props['data-status']);
    switch (props.type) {
      case 'order':
        return `${color}15`; // Light background
      case 'payment':
        return `${color}08`; // Very light background
      case 'delivery':
        return '#F8F8F8'; // Light grey background
      default:
        return `${color}15`;
    }
  }};
  box-shadow: ${(props: StyledProps) => {
    const color = getStatusColor(props['data-status']);
    switch (props.type) {
      case 'payment':
        return `0 0 0 1px ${color}40`; // Soft border
      default:
        return 'none';
    }
  }};
  position: relative;
`;

const StatusText = styled.span<StyledProps>`
  font-size: ${(props: StyledProps) => {
    switch (props.type) {
      case 'order':
        return '13px';
      case 'payment':
        return '12px';
      case 'delivery':
        return '11px';
      default:
        return '12px';
    }
  }};
  font-weight: ${(props: StyledProps) => {
    switch (props.type) {
      case 'order':
        return '500';
      case 'payment':
        return '600';
      case 'delivery':
        return '400';
      default:
        return '500';
    }
  }};
  text-transform: ${(props: StyledProps) => {
    switch (props.type) {
      case 'order':
        return 'capitalize';
      case 'payment':
        return 'capitalize';
      case 'delivery':
        return 'uppercase';
      default:
        return 'capitalize';
    }
  }};
  letter-spacing: ${(props: StyledProps) => (props.type === 'delivery' ? '0.5px' : 'normal')};
  color: ${(props: StyledProps) => getStatusColor(props['data-status'])};
`;

const IconWrapper = styled.div<StyledProps>`
  display: flex;
  align-items: center;
  transform: ${(props: StyledProps) => (props.type === 'delivery' ? 'scale(0.85)' : 'none')};
`;

const StatusDot: React.FC<StatusDotProps> = ({ status, type = 'order' }) => {
  const normalizedStatus = status?.toLowerCase();

  if (!normalizedStatus) return null;

  const getDot = () => {
    if (normalizedStatus.includes('pending')) {
      return <OrangeDot />;
    } else if (
      normalizedStatus.includes('completed') ||
      normalizedStatus.includes('ongoing') ||
      normalizedStatus.includes('success')
    ) {
      return <GreenDot />;
    } else if (
      normalizedStatus.includes('rejected') ||
      normalizedStatus.includes('cancelled') ||
      normalizedStatus.includes('failed')
    ) {
      return <RedDot />;
    }
    return null;
  };

  return (
    <StatusContainer type={type} data-status={status}>
      <IconWrapper type={type}>{getDot()}</IconWrapper>
      <StatusText type={type} data-status={status}>
        {status}
      </StatusText>
    </StatusContainer>
  );
};

export default StatusDot;
